// client/src/lib/contextCapture/contextCapture.ts

/**
 * Context Capture Service
 *
 * Intercepts and stores runtime context for debugging:
 * - Console logs (log, info, warn, error)
 * - Click trail (element descriptions)
 * - Network requests (fetch wrapper)
 * - Long tasks (PerformanceObserver)
 * - Navigation history
 *
 * All captured data is sanitized to remove sensitive information.
 *
 * @example
 * ```tsx
 * import { init, getCapturedContext, destroy } from './contextCapture';
 *
 * // Initialize on app start
 * init({ maxConsoleLogs: 100, maxClicks: 20 });
 *
 * // Get snapshot when submitting bug report
 * const context = getCapturedContext(userId, email, role, permissions);
 *
 * // Clean up on app unmount
 * destroy();
 * ```
 */

import type {
  CapturedContext,
  ConsoleLogEntry,
  ClickTrailEntry,
  NetworkRequestEntry,
  LongTaskEntry,
  NavStateSnapshot,
} from '../../types/issue-triage';

import {
  sanitizeUrl,
  sanitizeObject,
  sanitizeJsonString,
  scrubThenTruncate,
  truncate,
} from './sanitizer';
import { isOwnBodyCaptureUrl, type UrlParser } from '@officehub/shared';
import { fetchWithCallableRetry, isCallBudgetExceeded, type HedgeOutcome } from '../callableRetry';

/** The browser's own WHATWG parser, handed to the shared own-host check (shared has no URL typings). */
const parseUrl: UrlParser = (url, base) => new URL(url, base);

/** Upper bound on a 5xx body read for error reporting; the reporter caps it further. */
const MAX_ERROR_BODY_READ_CHARS = 8192;

/**
 * Read at most `max` characters of a response body, then cancel the stream, so a large or
 * never-ending error body is never pulled fully into memory. Falls back to text() only
 * where the platform exposes no body stream.
 */
async function readBoundedText(res: Response, max: number): Promise<string | undefined> {
  const reader = res.body?.getReader?.();
  if (!reader || typeof TextDecoder === 'undefined') {
    return (await res.text()).slice(0, max) || undefined;
  }
  const decoder = new TextDecoder();
  let out = '';
  try {
    while (out.length < max) {
      const { done, value } = await reader.read();
      if (done) break;
      out += decoder.decode(value, { stream: true });
    }
  } finally {
    reader.cancel().catch(() => undefined);
  }
  return out.slice(0, max) || undefined;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

export interface ContextCaptureConfig {
  maxConsoleLogs: number;
  maxClicks: number;
  maxNetworkRequests: number;
  maxNavigationHistory: number;
  maxLongTasks: number;
  maxStringLength: number;
  enabled: boolean;
  /**
   * Called when console.error or console.warn fires (for error reporting bridge).
   *
   * `error` is the FIRST `Error` instance among the console call's arguments, when
   * there is one, and it is the whole point of this third parameter.
   *
   * Before it existed the bridge received only the FORMATTED STRING, so the only
   * thing it could hand to the error reporter was a `new Error(message)` built
   * INSIDE this interceptor — carrying the interceptor's own stack. That made our
   * logging code the "most relevant frame" on every console-origin report, threw
   * away the real throw site, and collapsed unrelated crashes into one bucket.
   * Measured on live data (`.github/sweep-data/actionable-errors.json`): 4 of 16
   * captured client errors have `Object.d [as onConsoleError]` as their TOP STACK
   * FRAME, including a `FirebaseError: Invalid phone number` whose real origin is
   * an `onClick` two frames down.
   *
   * Optional and additive on purpose: a two-argument callback is still assignable,
   * so existing consumers are unaffected. Passing the Error costs nothing here —
   * it is already extracted below for the breadcrumb buffer's `stack` field and
   * was simply being discarded.
   */
  onConsoleError?: (message: string, level: 'error' | 'warning', error?: Error) => void;
  /** Called when fetch returns non-ok or network failure (for error reporting bridge) */
  /**
   * `responseBody` is passed only for a 5xx that `shouldCaptureErrorBody` approved, raw
   * and bounded; the consumer decides how to attach it. For an approved 5xx the call
   * arrives asynchronously, after the body read.
   */
  onNetworkError?: (url: string, status: number, method: string, duration: number, responseBody?: string) => void;
  /**
   * Decides, BEFORE anything is read, whether a 5xx's body should be read for
   * `onNetworkError`. Absent = never read an error body.
   */
  shouldCaptureErrorBody?: (url: string, status: number) => boolean;
  /**
   * Called when fetch returns an ok response. Lets the reporting bridge tell a
   * one-off failure from one that keeps happening (Sentry OFFICEHUB-CLIENT-68).
   */
  onNetworkSuccess?: (url: string) => void;
  /**
   * Called once when a hedged QuickBooks read that needed a second attempt settles (lib/callableRetry.ts): which attempt answered and
   * after how long, so the error reports that follow show it (error group f8f7e3503c27e2cb).
   */
  onCallableHedge?: (outcome: HedgeOutcome) => void;
}

const DEFAULT_CONFIG: ContextCaptureConfig = {
  maxConsoleLogs: 100,
  maxClicks: 20,
  maxNetworkRequests: 30,
  maxNavigationHistory: 10,
  maxLongTasks: 20,
  maxStringLength: 2000,
  enabled: true,
};

// ============================================================================
// STATE
// ============================================================================

let config: ContextCaptureConfig = { ...DEFAULT_CONFIG };
let isInitialized = false;
let currentRoute = '/';

// Circular buffers
let consoleLogs: ConsoleLogEntry[] = [];
let clickTrail: ClickTrailEntry[] = [];
let networkRequests: NetworkRequestEntry[] = [];
let navigationHistory: string[] = [];
/** What the navigation was showing at the last update (ISS-544 diagnostic); null until the provider reports. */
let navState: Pick<NavStateSnapshot, 'currentModule' | 'navItemCount' | 'hasUser'> | null = null;
let longTasks: LongTaskEntry[] = [];

// Cleanup functions to restore originals
const cleanupFunctions: Array<() => void> = [];

// Original references (stored to restore on destroy)
let originalConsoleLog: typeof console.log;
let originalConsoleInfo: typeof console.info;
let originalConsoleWarn: typeof console.warn;
let originalConsoleError: typeof console.error;
let originalFetch: typeof window.fetch;
let performanceObserver: PerformanceObserver | null = null;

// ============================================================================
// CIRCULAR BUFFER HELPERS
// ============================================================================

function pushToBuffer<T>(buffer: T[], item: T, maxLength: number): T[] {
  buffer.push(item);
  if (buffer.length > maxLength) {
    buffer.shift();
  }
  return buffer;
}

// ============================================================================
// CONSOLE INTERCEPTION
// ============================================================================

function createConsoleInterceptor(
  level: ConsoleLogEntry['level'],
  originalFn: (...args: unknown[]) => void
): (...args: unknown[]) => void {
  return function (...args: unknown[]) {
    // Always call original
    originalFn.apply(console, args);

    // Only capture if enabled
    if (!config.enabled) return;

    try {
      // Format message
      const message = args
        .map((arg) => {
          if (typeof arg === 'string') {
            return scrubThenTruncate(arg, config.maxStringLength);
          }
          if (arg instanceof Error) {
            return `${arg.name}: ${arg.message}`; // not truncated here; the joined message is scrubbed below
          }
          try {
            const sanitized = sanitizeObject(arg);
            return scrubThenTruncate(JSON.stringify(sanitized, null, 0), config.maxStringLength);
          } catch {
            return '[Object]';
          }
        })
        .join(' ');

      // The first Error instance among the arguments, if any. Hoisted out of the
      // `level === 'error'` block below because it is now needed TWICE: once for
      // the breadcrumb buffer's stack field, and once to hand the real error to
      // the reporting bridge (see ContextCaptureConfig.onConsoleError).
      const errorArg = args.find((arg) => arg instanceof Error) as
        | Error
        | undefined;

      // Extract stack for errors
      let stack: string | undefined;
      if (level === 'error') {
        if (errorArg?.stack) {
          stack = scrubThenTruncate(errorArg.stack, config.maxStringLength);
        } else {
          // Capture current stack
          const err = new Error();
          if (err.stack) {
            // Remove first two lines (Error and this function)
            const lines = err.stack.split('\n').slice(2);
            stack = scrubThenTruncate(lines.join('\n'), config.maxStringLength);
          }
        }
      }

      const entry: ConsoleLogEntry = {
        level,
        message: scrubThenTruncate(message, config.maxStringLength),
        timestamp: Date.now(),
        ...(stack && { stack }),
      };

      consoleLogs = pushToBuffer(consoleLogs, entry, config.maxConsoleLogs);

      // Notify error reporting bridge for error/warn levels
      if ((level === 'error' || level === 'warn') && config.onConsoleError) {
        try {
          config.onConsoleError(
            entry.message,
            level === 'warn' ? 'warning' : 'error',
            errorArg
          );
        } catch {
          // Never break the app from callback failures
        }
      }
    } catch {
      // Silently fail - never break the app
    }
  };
}

function setupConsoleInterception(): void {
  // Store originals
  originalConsoleLog = console.log;
  originalConsoleInfo = console.info;
  originalConsoleWarn = console.warn;
  originalConsoleError = console.error;

  // Replace with interceptors
  console.log = createConsoleInterceptor('log', originalConsoleLog);
  console.info = createConsoleInterceptor('info', originalConsoleInfo);
  console.warn = createConsoleInterceptor('warn', originalConsoleWarn);
  console.error = createConsoleInterceptor('error', originalConsoleError);

  // Store cleanup
  cleanupFunctions.push(() => {
    console.log = originalConsoleLog;
    console.info = originalConsoleInfo;
    console.warn = originalConsoleWarn;
    console.error = originalConsoleError;
  });
}

// ============================================================================
// CLICK TRACKING
// ============================================================================

function getElementDescription(element: Element): string {
  const tagName = element.tagName.toLowerCase();
  const id = element.id ? `#${element.id}` : '';
  const classes = element.className
    ? `.${String(element.className).split(' ').filter(Boolean).slice(0, 3).join('.')}`
    : '';
  const role = element.getAttribute('role')
    ? `[role="${element.getAttribute('role')}"]`
    : '';
  const type =
    element.getAttribute('type') && tagName === 'input'
      ? `[type="${element.getAttribute('type')}"]`
      : '';
  const ariaLabel = element.getAttribute('aria-label')
    ? `[aria-label="${truncate(element.getAttribute('aria-label') || '', 30)}"]`
    : '';

  return `${tagName}${id}${classes}${role}${type}${ariaLabel}`;
}

function getElementText(element: Element): string {
  // For buttons and links, get the text content
  const text =
    element.textContent?.trim() ||
    element.getAttribute('aria-label') ||
    element.getAttribute('title') ||
    '';
  return truncate(text, 50);
}

function handleClick(event: MouseEvent): void {
  if (!config.enabled) return;

  try {
    const target = event.target as Element;
    if (!target) return;

    // Only track interactive elements
    const isInteractive =
      target.tagName === 'BUTTON' ||
      target.tagName === 'A' ||
      target.tagName === 'INPUT' ||
      target.tagName === 'SELECT' ||
      target.tagName === 'TEXTAREA' ||
      target.closest('button') ||
      target.closest('a') ||
      target.getAttribute('role') === 'button' ||
      target.getAttribute('role') === 'link' ||
      target.getAttribute('tabindex') !== null;

    if (!isInteractive) return;

    // Find the actual interactive element
    const interactiveElement =
      target.tagName === 'BUTTON' ||
      target.tagName === 'A' ||
      target.getAttribute('role') === 'button'
        ? target
        : target.closest('button') ||
          target.closest('a') ||
          target.closest('[role="button"]') ||
          target;

    const entry: ClickTrailEntry = {
      element: getElementDescription(interactiveElement),
      text: getElementText(interactiveElement),
      timestamp: Date.now(),
      path: currentRoute,
    };

    clickTrail = pushToBuffer(clickTrail, entry, config.maxClicks);
  } catch {
    // Silently fail
  }
}

function setupClickTracking(): void {
  document.addEventListener('click', handleClick, { capture: true });

  cleanupFunctions.push(() => {
    document.removeEventListener('click', handleClick, { capture: true });
  });
}

// ============================================================================
// NETWORK INTERCEPTION
// ============================================================================

async function wrappedFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  // Get URL
  let url: string;
  if (typeof input === 'string') {
    url = input;
  } else if (input instanceof URL) {
    url = input.toString();
  } else {
    url = input.url;
  }

  // If capture disabled, just call original (a retry-safe callable that lost its request is still retried)
  if (!config.enabled) {
    return fetchWithCallableRetry(originalFetch, input, init, { onHedgeOutcome: config.onCallableHedge });
  }

  const startTime = Date.now();
  const method = init?.method || 'GET';
  const sanitizedUrl = sanitizeUrl(url);

  // Capture request body (if present and not FormData)
  // Bodies are kept ONLY for our own hosts (one list, @officehub/shared); a sign-in, OAuth or any other third-party
  // call is recorded as method/url/status without either body (#7643, #7644).
  const credentialExchange = !isOwnBodyCaptureUrl(url, parseUrl);
  let requestBody: string | undefined;
  if (!credentialExchange && init?.body && typeof init.body === 'string') {
    requestBody = sanitizeJsonString(init.body, config.maxStringLength);
  }

  try {
    // A retry-safe callable that got no answer is retried inside this call (callableRetry.ts), so
    // a request that recovers is never reported as a network error. Each failed attempt that was
    // retried is kept in the request buffer so a later real error still shows the history.
    const response = await fetchWithCallableRetry(originalFetch, input, init, {
      onHedgeOutcome: config.onCallableHedge,
      onAttemptFailed: (_attemptUrl, attempt) => {
        networkRequests = pushToBuffer(
          networkRequests,
          {
            method,
            url: sanitizedUrl,
            status: 0,
            duration: Date.now() - startTime,
            timestamp: startTime,
            responseBody: `No response (attempt ${attempt}); retrying`,
          },
          config.maxNetworkRequests
        );
      },
    });
    const duration = Date.now() - startTime;

    // Clone response to read body without consuming it
    const clonedResponse = response.clone();
    let responseBody: string | undefined;

    try {
      // Only capture JSON responses
      const contentType = response.headers.get('content-type') || '';
      if (!credentialExchange && contentType.includes('application/json')) {
        const text = await clonedResponse.text();
        responseBody = sanitizeJsonString(text, config.maxStringLength);
      }
    } catch {
      // Ignore body capture errors
    }

    const entry: NetworkRequestEntry = {
      method,
      url: sanitizedUrl,
      status: response.status,
      duration,
      timestamp: startTime,
      ...(requestBody && { requestBody }),
      ...(responseBody && { responseBody }),
    };

    networkRequests = pushToBuffer(
      networkRequests,
      entry,
      config.maxNetworkRequests
    );

    // Notify error reporting bridge for non-ok responses
    if (!response.ok && config.onNetworkError) {
      const onNetworkError = config.onNetworkError;
      const status = response.status;
      const notify = (errorBody?: string): void => {
        try {
          if (errorBody !== undefined) {
            onNetworkError(sanitizedUrl, status, method, duration, errorBody);
          } else {
            onNetworkError(sanitizedUrl, status, method, duration);
          }
        } catch {
          // Never break the app from callback failures
        }
      };
      // A 5xx body is where our functions put the root cause (#3721). Whether to read
      // it is decided BEFORE any read, by the consumer (a third party's body is never
      // touched). The read runs in the background so the caller's fetch is never held
      // by it, and stops at MAX_ERROR_BODY_READ_CHARS instead of downloading it all.
      if (status >= 500 && config.shouldCaptureErrorBody?.(sanitizedUrl, status)) {
        const clone = response.clone(); // synchronously, before the caller can consume it
        void readBoundedText(clone, MAX_ERROR_BODY_READ_CHARS).then(notify, () => notify());
      } else {
        notify();
      }
    }
    if (response.ok && config.onNetworkSuccess) {
      try {
        config.onNetworkSuccess(sanitizedUrl);
      } catch {
        // Never break the app from callback failures
      }
    }

    return response;
  } catch (error) {
    // Capture failed request
    const duration = Date.now() - startTime;

    const entry: NetworkRequestEntry = {
      method,
      url: sanitizedUrl,
      status: 0, // Indicate network failure
      duration,
      timestamp: startTime,
      ...(requestBody && { requestBody }),
      responseBody:
        error instanceof Error
          ? scrubThenTruncate(`Error: ${error.message}`, config.maxStringLength)
          : 'Network request failed',
    };

    networkRequests = pushToBuffer(
      networkRequests,
      entry,
      config.maxNetworkRequests
    );

    // Notify error reporting bridge for network failures. Not for a hedged call that ran out its budget: the SDK already gave the
    // caller "deadline-exceeded" for it, so a status-0 report here would count the same event twice (callableRetry.ts).
    if (config.onNetworkError && !isCallBudgetExceeded(error)) {
      try {
        config.onNetworkError(sanitizedUrl, 0, method, duration);
      } catch {
        // Never break the app from callback failures
      }
    }

    throw error;
  }
}

/**
 * The fetch that was installed BEFORE this module wrapped it (or the live one when the capture is
 * not running). For a request that is DESIGNED to fail at the browser -- the wake-on-screen knock
 * (packages/offline-sdk wakeCallables) -- going through `wrappedFetch` would log each expected
 * failure as a status-0 network error on one of our callables and feed the transport-failure burst
 * counter. Not for ordinary requests: those want the wrapper's capture and callable retry.
 */
export function getOriginalFetch(): typeof window.fetch {
  return originalFetch ?? window.fetch.bind(window);
}

function setupNetworkInterception(): void {
  originalFetch = window.fetch.bind(window);
  window.fetch = wrappedFetch;

  cleanupFunctions.push(() => {
    window.fetch = originalFetch;
  });
}

// ============================================================================
// LONG TASK TRACKING
// ============================================================================

function setupLongTaskTracking(): void {
  // Check if PerformanceObserver is supported
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }

  // Check if 'longtask' entry type is supported
  try {
    const supported = PerformanceObserver.supportedEntryTypes;
    if (!supported.includes('longtask')) {
      return;
    }
  } catch {
    return;
  }

  try {
    performanceObserver = new PerformanceObserver((list) => {
      if (!config.enabled) return;

      for (const entry of list.getEntries()) {
        const taskEntry: LongTaskEntry = {
          duration: entry.duration,
          timestamp: Date.now(),
        };

        longTasks = pushToBuffer(longTasks, taskEntry, config.maxLongTasks);
      }
    });

    performanceObserver.observe({ entryTypes: ['longtask'] });

    cleanupFunctions.push(() => {
      if (performanceObserver) {
        performanceObserver.disconnect();
        performanceObserver = null;
      }
    });
  } catch {
    // Silently fail if long task observation not supported
  }
}

// ============================================================================
// BROWSER/OS DETECTION
// ============================================================================

function getBrowserInfo(): string {
  const ua = navigator.userAgent;

  if (ua.includes('Firefox/')) {
    const match = ua.match(/Firefox\/(\d+)/);
    return `Firefox ${match?.[1] || ''}`;
  }
  if (ua.includes('Edg/')) {
    const match = ua.match(/Edg\/(\d+)/);
    return `Edge ${match?.[1] || ''}`;
  }
  if (ua.includes('Chrome/')) {
    const match = ua.match(/Chrome\/(\d+)/);
    return `Chrome ${match?.[1] || ''}`;
  }
  if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    const match = ua.match(/Version\/(\d+)/);
    return `Safari ${match?.[1] || ''}`;
  }

  return 'Unknown';
}

function getOSInfo(): string {
  const ua = navigator.userAgent;

  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';

  return 'Unknown';
}

// ============================================================================
// REACT QUERY CACHE KEYS
// ============================================================================

function getReactQueryCacheKeys(): string[] {
  try {
    // Try to find React Query cache in window (if exposed for debugging)
    // This is a best-effort approach
    const windowAny = window as unknown as Record<string, unknown>;

    // Check for React Query Devtools exposed cache
    if (windowAny.__REACT_QUERY_DEVTOOLS_GLOBAL_STORE__) {
      const store = windowAny.__REACT_QUERY_DEVTOOLS_GLOBAL_STORE__ as {
        getQueries?: () => Array<{ queryKey: unknown }>;
      };
      if (typeof store.getQueries === 'function') {
        const queries = store.getQueries();
        return queries.map((q) => JSON.stringify(q.queryKey)).slice(0, 50);
      }
    }

    // Alternative: Check for tanstack query
    if (
      windowAny.__TANSTACK_QUERY_GLOBAL_STORE__ ||
      windowAny.__REACT_QUERY_GLOBAL_STORE__
    ) {
      const store = (windowAny.__TANSTACK_QUERY_GLOBAL_STORE__ ||
        windowAny.__REACT_QUERY_GLOBAL_STORE__) as {
        getQueryCache?: () => {
          getAll: () => Array<{ queryKey: unknown }>;
        };
      };
      if (typeof store.getQueryCache === 'function') {
        const cache = store.getQueryCache();
        if (typeof cache.getAll === 'function') {
          const queries = cache.getAll();
          return queries.map((q) => JSON.stringify(q.queryKey)).slice(0, 50);
        }
      }
    }

    return [];
  } catch {
    return [];
  }
}

// ============================================================================
// LOCAL STORAGE KEYS
// ============================================================================

function getLocalStorageKeys(): string[] {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }
    return keys.slice(0, 50);
  } catch {
    return [];
  }
}

// ============================================================================
// MEMORY USAGE
// ============================================================================

function getMemoryUsage(): number | undefined {
  try {
    // Chrome-specific API
    const performanceAny = performance as unknown as {
      memory?: { usedJSHeapSize: number };
    };
    if (performanceAny.memory) {
      return performanceAny.memory.usedJSHeapSize;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Initialize the context capture service
 *
 * @param customConfig - Optional configuration overrides
 */
export function init(customConfig?: Partial<ContextCaptureConfig>): void {
  if (isInitialized) {
    console.warn('Context capture already initialized');
    return;
  }

  // Merge config
  config = { ...DEFAULT_CONFIG, ...customConfig };

  // Set up all interceptions
  setupConsoleInterception();
  setupClickTracking();
  setupNetworkInterception();
  setupLongTaskTracking();

  isInitialized = true;
}

/**
 * Clean up and restore original functions
 */
export function destroy(): void {
  if (!isInitialized) return;

  // Run all cleanup functions
  for (const cleanup of cleanupFunctions) {
    try {
      cleanup();
    } catch {
      // Ignore cleanup errors
    }
  }

  // Clear cleanup list
  cleanupFunctions.length = 0;

  // Clear buffers
  clear();

  isInitialized = false;
}

/**
 * Update the current route (call on route changes)
 *
 * @param route - Current route path
 */
export function setRoute(route: string): void {
  if (currentRoute !== route) {
    currentRoute = route;

    if (config.enabled) {
      navigationHistory = pushToBuffer(
        navigationHistory,
        route,
        config.maxNavigationHistory
      );
    }
  }
}

/**
 * Record what the app's navigation is showing right now (called by ModuleProvider whenever any of the
 * three changes). Only ids and counts: no user data, no free text.
 *
 * @param state - The current module id (or null), the number of nav tabs, and whether a user exists
 */
export function setNavState(state: Pick<NavStateSnapshot, 'currentModule' | 'navItemCount' | 'hasUser'>): void {
  // Copy the three known members by name: whatever else a caller hands in is never kept.
  navState = { currentModule: state.currentModule, navItemCount: state.navItemCount, hasUser: state.hasUser };
}

/**
 * Forget the navigation state (the provider that reported it has gone away). A report filed after that
 * must say "never reported" (null), not repeat a stale "user present, module P12".
 */
export function clearNavState(): void {
  navState = null;
}

/**
 * Enable or disable context capture
 *
 * @param enabled - Whether capture should be enabled
 */
export function setEnabled(enabled: boolean): void {
  config.enabled = enabled;
}

/**
 * Check if context capture is enabled
 */
export function isEnabled(): boolean {
  return config.enabled;
}

/**
 * Clear all captured data
 */
export function clear(): void {
  consoleLogs = [];
  clickTrail = [];
  networkRequests = [];
  navigationHistory = [];
  longTasks = [];
  navState = null;
}

/**
 * Get a snapshot of all captured context
 *
 * @param userId - Current user's ID
 * @param email - Current user's email
 * @param role - Current user's role
 * @param permissions - Current user's permissions
 * @returns Captured context snapshot
 */
export function getCapturedContext(
  userId: string,
  email: string,
  role: string,
  permissions: string[]
): CapturedContext {
  return {
    // Location
    url: sanitizeUrl(window.location.href),
    route: currentRoute,
    navigationHistory: [...navigationHistory],

    // Environment
    browser: getBrowserInfo(),
    os: getOSInfo(),
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    scrollPosition: {
      x: window.scrollX,
      y: window.scrollY,
    },

    // User
    userId,
    email,
    role,
    permissions: [...permissions],

    // Logs & Events
    consoleLogs: [...consoleLogs],
    clickTrail: [...clickTrail],
    networkRequests: [...networkRequests],

    // App State
    reactQueryCacheKeys: getReactQueryCacheKeys(),
    localStorageKeys: getLocalStorageKeys(),

    // Performance
    memoryUsage: getMemoryUsage(),
    longTasks: [...longTasks],

    // Navigation state (ISS-544): previousRoute is the entry before the current one in the route history
    navState: navState ?
      { ...navState, previousRoute: navigationHistory.length >= 2 ? navigationHistory[navigationHistory.length - 2] : null } :
      null,
  };
}

/**
 * Manually add a console log entry (for testing)
 */
export function addConsoleLog(entry: ConsoleLogEntry): void {
  consoleLogs = pushToBuffer(consoleLogs, entry, config.maxConsoleLogs);
}

/**
 * Get current buffer sizes (for debugging)
 */
export function getBufferStats(): {
  consoleLogs: number;
  clickTrail: number;
  networkRequests: number;
  navigationHistory: number;
  longTasks: number;
} {
  return {
    consoleLogs: consoleLogs.length,
    clickTrail: clickTrail.length,
    networkRequests: networkRequests.length,
    navigationHistory: navigationHistory.length,
    longTasks: longTasks.length,
  };
}
